import { ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import {
  NEXT_STEP_CTA_KEYS,
  ONBOARDING_VERIFICATION_ROUTE,
  resolveRejectionDescription,
} from "../constants/verification.constants";
import type { OrganizationVerificationStatus } from "../hooks/useOrganizationVerificationStatus";
import type { KycStatus } from "../types/verification-api.types";
import { KycStatusPill } from "./KycStatusPill";

export interface PayoutsVerificationGateProps {
  status: OrganizationVerificationStatus;
}

/**
 * Replaces the dispersions wizard while `capabilities["payouts.create"].allowed` is false: the current
 * status, why money cannot leave yet, and the next step. The server decides — this only renders its verdict.
 */
export function PayoutsVerificationGate({ status }: PayoutsVerificationGateProps) {
  const { t } = useTranslations("verification");
  const navigate = useNavigate();

  const kycStatus: KycStatus = status.kycStatus ?? "PENDING";
  const description = !status.hasCorporateIdentity
    ? t("payouts_blocked_start_description")
    : kycStatus === "IN_REVIEW"
      ? t("payouts_blocked_in_review_description")
      : kycStatus === "REJECTED"
        ? resolveRejectionDescription(status.rejectionReason, t)
        : t("payouts_blocked_description");
  const ctaKey = NEXT_STEP_CTA_KEYS[status.nextStep];

  return (
    <ColocaSurface variant="panel" className="p-6">
      <div className="mb-4 flex justify-center">
        <KycStatusPill status={kycStatus} />
      </div>
      <ColocaEmpty
        surface="card"
        icon={ShieldAlert}
        title={t("payouts_blocked_title")}
        description={description}
      >
        <div className="flex flex-wrap justify-center gap-2">
          {ctaKey && (
            <ColocaButton variant="primary" onClick={() => navigate(ONBOARDING_VERIFICATION_ROUTE)}>
              {t(ctaKey)}
            </ColocaButton>
          )}
          <ColocaButton variant="ghost" onClick={() => navigate("/app/wallets")}>
            {t("payouts_blocked_back_wallets")}
          </ColocaButton>
        </div>
      </ColocaEmpty>
    </ColocaSurface>
  );
}
